import React, { useState } from 'react';
import {
  Button,
  Stack,
  Menu,
  MenuItem,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  KeyboardArrowDown,
  ContentCopy,
  Archive,
  Edit,
  Delete,
  CloudUpload,
  CloudDownload,
  Visibility,
} from '@mui/icons-material';

const buttonBaseStyle = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  borderRadius: '8px',
  height: '40px',
  px: 2,
};

const menuPaperStyle = {
  mt: 1,
  borderRadius: '8px',
  boxShadow:
    '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
  border: '1px solid #F2F4F7',
  minWidth: 180,
};

const menuItemStyle = { fontSize: '14px', py: 1.25 };
const menuIconStyle = { fontSize: 18, mr: 1.5, color: '#667085' };

const ToolbarButton = ({
  label,
  icon: Icon,
  onClick,
  variant = 'outlined',
  color = 'secondary',
  disabled = false,
  title,
  disabledTitle,
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Button
      variant={variant}
      color={color}
      disabled={disabled}
      onClick={onClick}
      startIcon={Icon ? <Icon sx={{ fontSize: 18 }} /> : undefined}
      title={disabled && disabledTitle ? disabledTitle : title}
      sx={isSmall ? { ...buttonBaseStyle, minWidth: 0, px: 1 } : buttonBaseStyle}
    >
      {!isSmall && label}
    </Button>
  );
};

const MoreMenu = ({ items }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleItemClick = (onClick) => {
    handleClose();
    setTimeout(() => onClick?.(), 0);
  };

  return (
    <Box>
      <Button
        variant="outlined"
        color="secondary"
        onClick={handleOpen}
        endIcon={<KeyboardArrowDown />}
        sx={buttonBaseStyle}
      >
        More
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ sx: menuPaperStyle }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {items.map(({ label, icon: Icon, onClick, disabled }) => (
          <MenuItem
            key={label}
            disabled={disabled}
            onClick={() => handleItemClick(onClick)}
            sx={menuItemStyle}
          >
            {Icon && <Icon sx={menuIconStyle} />}
            {label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

// ─── Composed toolbars ────────────────────────────────────────────────────────

/**
 * TestCaseToolbar
 *
 * Props:
 *   onAdd            - open the create test case modal
 *   onBulkEdit       - open the bulk edit modal
 *   onClone          - open clone confirmation
 *   onArchive        - open archive confirmation (selected items)
 *   onUnarchive      - unarchive selected items (archived view only)
 *   onImport         - open CSV import modal
 *   onExport         - open CSV export modal
 *   onToggleArchived - toggle between normal / archived view
 *   showArchived     - whether the archived view is active
 *   isSelectedItems  - whether any rows are currently selected
 */
export const TestCaseToolbar = ({
  onAdd,
  onBulkEdit,
  onClone,
  onArchive,
  onUnarchive,
  onImport,
  onExport,
  onToggleArchived,
  showArchived,
  isSelectedItems,
}) => {
  const noSelectionHint = 'Select test cases to perform this action';

  // Archived view — matches original: Show All + Unarchive only (no Delete)
  if (showArchived) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <ToolbarButton
          label="Show All"
          icon={Visibility}
          variant="contained"
          color="primary"
          onClick={onToggleArchived}
        />
        <ToolbarButton
          label="Unarchive"
          icon={Archive}
          disabled={!isSelectedItems}
          disabledTitle={noSelectionHint}
          title="Unarchive selected test cases"
          onClick={onUnarchive}
        />
      </Stack>
    );
  }

  // Normal view — matches original: Add / Bulk Edit / Clone / More (dropdown)
  // "View Archived" and "Archive Selected" live in the More menu, matching the original.
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <ToolbarButton
        label="Add Test Case"
        icon={Add}
        variant="contained"
        color="primary"
        onClick={onAdd}
      />
      <ToolbarButton
        label="Bulk Edit"
        icon={Edit}
        disabled={!isSelectedItems}
        disabledTitle={noSelectionHint}
        title="Bulk Edit"
        onClick={onBulkEdit}
      />
      <ToolbarButton
        label="Clone"
        icon={ContentCopy}
        disabled={!isSelectedItems}
        disabledTitle={noSelectionHint}
        title="Clone selected test cases"
        onClick={onClone}
      />
      <MoreMenu
        items={[
          {
            label: 'View Archived',
            icon: Archive,
            onClick: onToggleArchived,
          },
          {
            label: 'Archive Selected',
            icon: Archive,
            disabled: !isSelectedItems,
            onClick: onArchive,
          },
          {
            label: 'Import from CSV',
            icon: CloudUpload,
            onClick: onImport,
          },
          {
            label: 'Export to CSV',
            icon: CloudDownload,
            onClick: onExport,
          },
        ]}
      />
    </Stack>
  );
};

/**
 * TestCycleToolbar
 *
 * Props:
 *   onAdd           - open the create test cycle modal
 *   onClone         - open clone modal (requires exactly 1 selected)
 *   onDelete        - open delete confirmation
 *   isSelectedItems - number of selected rows
 */
export const TestCycleToolbar = ({
  onAdd,
  onClone,
  onDelete,
  isSelectedItems,
}) => (
  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
    <ToolbarButton
      label="Add Test Cycle"
      icon={Add}
      variant="contained"
      color="primary"
      onClick={onAdd}
    />
    <ToolbarButton
      label="Clone"
      icon={ContentCopy}
      disabled={isSelectedItems !== 1}
      disabledTitle="Select one test cycle to perform this action"
      title="Clone"
      onClick={onClone}
    />
    <ToolbarButton
      label="Delete"
      icon={Delete}
      color="error"
      disabled={!isSelectedItems}
      disabledTitle="Select test cycles to perform this action"
      title="Delete"
      onClick={onDelete}
    />
  </Stack>
);